import React from 'react';
import { useFormattedContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { BroadcastIcon, FullySoldIcon, LightingBoltIcon } from 'src/svgs';
import { FloorIcon } from 'src/svgs/FloorIcon';
import { RankPremiumIcon } from 'src/svgs/RankPremiumIcon';
import { TicketOutlineIcon } from 'src/svgs/TicketOutlineIcon';
import { WarningSolidIcon } from 'src/svgs/Viagogo';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { formatCurrency, formatPercent } from 'src/utils/numberFormatter';

import * as styles from './RollupMetrics.css';

export const BaseRollupMetrics = ({
  children,
  tooltipContent,
  styleOverride,
}: {
  children: React.ReactNode;
  tooltipContent?: string;
  styleOverride?: React.CSSProperties;
}) => {
  const { isDarkMode } = useSiteTheme();
  if (!tooltipContent) {
    return (
      <Stack
        className={styles.baseRollupMetrics}
        alignItems="center"
        justifyContent="center"
        gap="s"
        width="full"
        style={styleOverride}
      >
        {children}
      </Stack>
    );
  }

  return (
    <Tooltip
      contentVariant={isDarkMode ? 'light' : 'darkGray'}
      triggerContent={
        <Stack
          className={styles.baseRollupMetrics}
          alignItems="center"
          justifyContent="center"
          gap="s"
          width="full"
          style={styleOverride}
        >
          {children}
        </Stack>
      }
      align="center"
    >
      {tooltipContent}
    </Tooltip>
  );
};

export const SectionsRollupMetrics = ({ sections }: { sections: string[] }) => {
  const displaySections = sections.join(', ');
  return (
    <BaseRollupMetrics
      tooltipContent={displaySections}
      styleOverride={{
        minWidth: 20,
      }}
    >
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displaySections}
      </div>
    </BaseRollupMetrics>
  );
};

export const BroadcastStatusRollupMetrics = ({
  activeBroadcastCount,
  soldCount,
  totalListingCount,
}: {
  activeBroadcastCount: number;
  soldCount: number;
  totalListingCount: number;
}) => {
  const { isDarkMode } = useSiteTheme();

  const broadcastStatusTooltip = useFormattedContent(
    FormatContentId.BroadcastStatusRollupMetric,
    [activeBroadcastCount.toString(), totalListingCount.toString()]
  ) as string;

  const soldListingsTooltip = useFormattedContent(
    FormatContentId.SoldListingsRollupMetric,
    [soldCount.toString()]
  ) as string;

  if (soldCount > 0) {
    return (
      <Stack
        className={styles.split}
        alignItems="center"
        justifyContent="center"
      >
        <Tooltip
          contentVariant={isDarkMode ? 'light' : 'darkGray'}
          triggerContent={
            <Stack
              className={styles.broadcastRollupMetricsFirst}
              alignItems="center"
              justifyContent="center"
              gap="s"
            >
              <BroadcastIcon size={vars.iconSize.xs} />
              {`${activeBroadcastCount} of ${totalListingCount}`}
            </Stack>
          }
          align="center"
        >
          {broadcastStatusTooltip}
        </Tooltip>
        <Tooltip
          contentVariant={isDarkMode ? 'light' : 'darkGray'}
          triggerContent={
            <Stack
              className={styles.broadcastRollupMetricsLast}
              alignItems="center"
              justifyContent="center"
              gap="s"
            >
              <FullySoldIcon
                size={vars.iconSize.xs}
                fill={vars.color.iconDisabled}
              />
              {soldCount}
            </Stack>
          }
          align="center"
        >
          {soldListingsTooltip}
        </Tooltip>
      </Stack>
    );
  }
  return (
    <BaseRollupMetrics tooltipContent={broadcastStatusTooltip}>
      <BroadcastIcon size={vars.iconSize.xs} />
      {`${activeBroadcastCount} of ${totalListingCount}`}
    </BaseRollupMetrics>
  );
};

export const AutopriceCountRollupMetrics = ({
  isAutoPricedCount,
}: {
  isAutoPricedCount: number;
}) => {
  const tooltipContent = useFormattedContent(
    FormatContentId.AutoPricedListingsRollupMetric,
    [isAutoPricedCount.toString()]
  ) as string;
  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <LightingBoltIcon fill={vars.color.iconBrand} size={vars.iconSize.xs} />
      {isAutoPricedCount}
    </BaseRollupMetrics>
  );
};

export const PriceConflictRollupMetrics = ({
  priceConflictCount,
}: {
  priceConflictCount: number;
}) => {
  const tooltipContent = useFormattedContent(
    FormatContentId.PriceConflictRollupMetric,
    [priceConflictCount.toString()]
  ) as string;
  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <WarningSolidIcon fill={vars.color.iconError} size={vars.iconSize.s} />
      {priceConflictCount}
    </BaseRollupMetrics>
  );
};

export const FloorPriceRollupMetrics = ({
  floorPrice,
  currencyCode,
}: {
  floorPrice: number;
  currencyCode: string | null;
}) => {
  const tooltipContent = useFormattedContent(
    FormatContentId.FloorPriceRollupMetric,
    [floorPrice.toString()]
  ) as string;
  const formattedPrice = formatCurrency(floorPrice, currencyCode ?? 'USD');
  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <FloorIcon size={vars.iconSize.xs} />
      {formattedPrice}
    </BaseRollupMetrics>
  );
};

export const SellThroughRateRollupMetrics = ({
  sellThroughRate,
}: {
  sellThroughRate: number | null;
}) => {
  const sellThroughRateFormatted = formatPercent(sellThroughRate ?? 0, 1);

  const tooltipContent = useFormattedContent(
    FormatContentId.SellThroughRateRollupMetric,
    [sellThroughRateFormatted]
  ) as string;
  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <div>{'STR: ' + sellThroughRateFormatted}</div>
    </BaseRollupMetrics>
  );
};

export const RankPremiumRollupMetrics = ({
  relativeRankPremium,
  absoluteRankPremium,
}: {
  relativeRankPremium?: number | null;
  absoluteRankPremium?: number | null;
}) => {
  let rankPremiumFormatted = '';
  if (absoluteRankPremium != null) {
    rankPremiumFormatted = formatCurrency(absoluteRankPremium, 'USD');
  } else {
    rankPremiumFormatted = formatPercent(relativeRankPremium ?? 0, 1);
  }
  const tooltipContent = useFormattedContent(
    FormatContentId.RankPremiumRollupMetric,
    [rankPremiumFormatted]
  ) as string;
  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <RankPremiumIcon size={vars.iconSize.xxs} />
      {rankPremiumFormatted}
    </BaseRollupMetrics>
  );
};

export const WebsitePriceRollupMetrics = ({
  websitePrice,
  currencyCode,
}: {
  websitePrice: number | null;
  currencyCode?: string | null;
}) => {
  const formattedPrice = formatCurrency(websitePrice, currencyCode ?? 'USD');

  const tooltipContent = useFormattedContent(
    FormatContentId.WebsitePriceRollupMetric,
    [formattedPrice ?? '0.00']
  ) as string;

  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <TicketOutlineIcon size={'10px'} />
      <span>{formattedPrice}</span>
    </BaseRollupMetrics>
  );
};

export const CountRollupMetrics = ({ count }: { count: number }) => {
  const tooltipContent = useFormattedContent(
    FormatContentId.SoldTicketsMetric,
    [count.toString()]
  ) as string;

  return (
    <BaseRollupMetrics tooltipContent={tooltipContent}>
      <div>{count}</div>
    </BaseRollupMetrics>
  );
};
